import React, { useEffect, useMemo, useState } from "react";
import { useData } from "../../hooks/useData";
import { MetricStrip } from "../common/MetricStrip";
import { EmptyState } from "../common/EmptyState";
import { Tag } from "../common/Tag";
import { fmtDate, relTime } from "../../utils/helpers";
import { userDashboardServices } from "../../APIServices/userDashboardServices";
import { useToast } from "../../hooks/useToast";

function RiskTrendChart({ points = [] }) {
  const safePoints = points.length ? points : Array.from({ length: 30 }, () => 0);
  const days = safePoints.length || 30;
  const w = 720;
  const h = 130;
  const pad = 28;
  const maxCount = Math.max(1, ...safePoints);
  const xStep = (w - pad * 2) / Math.max(days - 1, 1);

  const linePath = safePoints
    .map((value, i) => {
      const x = pad + i * xStep;
      const y = h - pad - (value / maxCount) * (h - pad * 2);
      return `${i === 0 ? "M" : "L"} ${x} ${y}`;
    })
    .join(" ");

  const fillPath =
    linePath +
    ` L ${pad + (days - 1) * xStep} ${h - pad} L ${pad} ${h - pad} Z`;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      style={{ width: "100%", height: 130, display: "block" }}
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--accent)" stopOpacity="0.15" />
          <stop offset="1" stopColor="var(--accent)" stopOpacity="0" />
        </linearGradient>
      </defs>

      {[0.25, 0.5, 0.75].map((p, i) => (
        <line
          key={i}
          x1={pad}
          x2={w - pad}
          y1={h - pad - p * (h - pad * 2)}
          y2={h - pad - p * (h - pad * 2)}
          stroke="var(--line)"
          strokeDasharray="2,3"
        />
      ))}

      {linePath && <path d={fillPath} fill="url(#trendGrad)" />}
      {linePath && (
        <path d={linePath} fill="none" stroke="var(--accent)" strokeWidth="1.5" />
      )}

      {safePoints.map((value, i) => {
        const x = pad + i * xStep;
        const y = h - pad - (value / maxCount) * (h - pad * 2);
        return (
          <circle
            key={i}
            cx={x}
            cy={y}
            r="2.5"
            fill="var(--surface)"
            stroke="var(--accent)"
            strokeWidth="1.5"
          />
        );
      })}

      <text
        x={pad}
        y={h - 10}
        fontFamily="JetBrains Mono, monospace"
        fontSize="9"
        fill="var(--ink-3)"
      >
        Last 30 days
      </text>

      <text
        x={w - pad}
        y={h - 10}
        fontFamily="JetBrains Mono, monospace"
        fontSize="9"
        fill="var(--ink-3)"
        textAnchor="end"
      >
        Today
      </text>
    </svg>
  );
}

function DashboardModule({ onNavigate }) {
  const { currentUserData } = useData();
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    metrics: {},
    coverage: {
      documents: {},
      reviews: {},
    },
    riskTrend: [],
    topRisks: [],
    upcomingMilestones: [],
    recentActivity: [],
    recommendations: [],
  });

  const syncDashboardData = async () => {
    setLoading(true);
    try {
      const response = await userDashboardServices.getUserDashboardSummary();
      const payload = response?.data || {};
      setSummary({
        metrics: payload.metrics || {},
        coverage: payload.coverage || { documents: {}, reviews: {} },
        riskTrend: payload.riskTrend || [],
        topRisks: payload.topRisks || [],
        upcomingMilestones: payload.upcomingMilestones || [],
        recentActivity: payload.recentActivity || [],
        recommendations: payload.recommendations || [],
      });
    } catch (error) {
      toast("Unable to load dashboard data.", "warn");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    syncDashboardData();
  }, [currentUserData?.userId]);

  const handleNavigate = (moduleKey) => {
    if (!moduleKey || typeof onNavigate !== "function") return;
    onNavigate(moduleKey);
  };

  const resolveActivityModule = (activity) => {
    const explicit = String(activity?.moduleKey || "").trim();
    if (explicit) return explicit;

    const action = String(activity?.action || "").toLowerCase();
    if (action.includes("risk")) return "risk";
    if (action.includes("milestone") || action.includes("task") || action.includes("project")) return "project";
    if (action.includes("feasibility")) return "feasibility";
    if (action.includes("knowledge")) return "knowledge";
    if (action.includes("report")) return "reports";
    if (action.includes("protocol")) return "protocolReview";
    if (action.includes("csr_listing")) return "csrListingReview";
    if (action.includes("csr")) return "csrReview";
    return "dashboard";
  };

  const metrics = useMemo(() => {
    return [
      {
        label: "Active studies",
        value: summary.metrics?.activeStudies || 0,
        foot: `${Math.max((summary.metrics?.totalStudies || 0) - (summary.metrics?.activeStudies || 0), 0)} inactive or archived`,
        dir: (summary.metrics?.activeStudies || 0) > 0 ? "ok" : "warn",
      },
      {
        label: "Open risks",
        value: summary.metrics?.openRisks || 0,
        foot: `${summary.metrics?.highSeverityRisks || 0} critical/high`,
        dir: (summary.metrics?.highSeverityRisks || 0) > 0 ? "warn" : "ok",
      },
      {
        label: "Closed tasks",
        value: summary.metrics?.closedTasks || 0,
        foot: `${Math.max((summary.metrics?.totalTasks || 0) - (summary.metrics?.closedTasks || 0), 0)} pending`,
        dir: (summary.metrics?.closedTasks || 0) > 0 ? "ok" : "warn",
      },
      {
        label: "Pending approvals",
        value: summary.metrics?.pendingApprovals || 0,
        foot: "feasibility and review queue",
        dir: (summary.metrics?.pendingApprovals || 0) > 0 ? "warn" : "ok",
      },
      {
        label: "Knowledge approved",
        value: summary.metrics?.approvedKnowledge || 0,
        foot: `${Math.max((summary.metrics?.totalKnowledge || 0) - (summary.metrics?.approvedKnowledge || 0), 0)} under review`,
        dir: (summary.metrics?.approvedKnowledge || 0) > 0 ? "ok" : "warn",
      },
      {
        label: "Reports generated",
        value: summary.metrics?.generatedReports || 0,
        foot: `${summary.metrics?.approvedReports || 0} approved`,
        dir: (summary.metrics?.generatedReports || 0) > 0 ? "ok" : "warn",
      },
    ];
  }, [summary]);

  const coverageCards = useMemo(() => {
    const docs = summary.coverage?.documents || {};
    const reviews = summary.coverage?.reviews || {};

    return [
      {
        key: "studies",
        title: "Studies and documents",
        value: `${summary.coverage?.studies || 0} studies · ${docs.total || 0} records`,
        desc: `${docs.folders || 0} folders · ${docs.files || 0} files · ${docs.deleted || 0} deleted`,
        moduleKey: "studies",
      },
      {
        key: "reviews",
        title: "Review modules",
        value: `${reviews.total || 0} active reviews`,
        desc: `Protocol ${reviews.protocol || 0} · CSR ${reviews.csr || 0} · Listing ${reviews.listing || 0}`,
        moduleKey: "protocolReview",
      },
      {
        key: "knowledge",
        title: "Knowledge and reports",
        value: `${summary.metrics?.totalKnowledge || 0} knowledge · ${summary.metrics?.generatedReports || 0} reports`,
        desc: `${summary.metrics?.approvedKnowledge || 0} approved knowledge · ${summary.metrics?.approvedReports || 0} approved reports`,
        moduleKey: "knowledge",
      },
      {
        key: "action",
        title: "Action required",
        value: `${summary.coverage?.actionRequired || 0} pending items`,
        desc: `${summary.metrics?.pendingApprovals || 0} feasibility approvals · ${reviews.pending || 0} review drafts`,
        moduleKey: "feasibility",
      },
    ];
  }, [summary]);

  const displayName =
    [currentUserData?.firstName, currentUserData?.lastName].filter(Boolean).join(" ") ||
    currentUserData?.name ||
    "User";

  const todayLabel = new Date().toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const totalTrendEvents = (summary.riskTrend || []).reduce((acc, num) => acc + num, 0);

  const moduleCoverage = [
    { name: "Studies", count: summary.coverage?.studies || 0, variant: "ok", moduleKey: "studies" },
    { name: "Open risks", count: summary.metrics?.openRisks || 0, variant: "warn", moduleKey: "risk" },
    { name: "Tasks", count: summary.metrics?.totalTasks || 0, variant: "default", moduleKey: "project" },
    { name: "Feasibility", count: summary.metrics?.pendingApprovals || 0, variant: "info", moduleKey: "feasibility" },
    { name: "Protocol review", count: summary.coverage?.reviews?.protocol || 0, variant: "warn", moduleKey: "protocolReview" },
    { name: "CSR review", count: summary.coverage?.reviews?.csr || 0, variant: "warn", moduleKey: "csrReview" },
    { name: "CSR listing", count: summary.coverage?.reviews?.listing || 0, variant: "warn", moduleKey: "csrListingReview" },
    { name: "Knowledge library", count: summary.metrics?.totalKnowledge || 0, variant: "danger", moduleKey: "knowledge" },
    { name: "Reports", count: summary.metrics?.generatedReports || 0, variant: "default", moduleKey: "reports" },
  ];

  return (
    <div className="dashboard-static">
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Portfolio overview - {todayLabel}</div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-sub">Welcome back, {displayName}. Here is where your portfolio stands.</p>
        </div>

        <div className="flex gap-2">
          <button className="btn" type="button" onClick={syncDashboardData} disabled={loading}>
            <i className="ti ti-refresh" />
            Refresh
          </button>
          <button className="btn btn-primary" type="button" onClick={() => handleNavigate("reports")}>
            <i className="ti ti-file-text" />
            Open reports
          </button>
        </div>
      </div>

      <MetricStrip items={metrics} />

      <div className="section-label mt-4 mb-5">Project data coverage</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        {coverageCards.map((card) => (
          <div
            className="card card-pad clickable-coverage-card"
            key={card.key}
            onClick={() => handleNavigate(card.moduleKey)}
          >
            <div className="xs muted" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span>{card.title}</span>
              <i className="ti ti-chevron-right clickable-row-arrow" />
            </div>
            <div className="medium" style={{ marginTop: 6 }}>
              {card.value}
            </div>
            <div className="xs muted" style={{ marginTop: 4 }}>{card.desc}</div>
          </div>
        ))}
      </div>

      <div className="row mt-4">
        <div className="col">
          <div className="section-label mb-5">Risk trend - 30-day activity</div>
          <div className="card card-pad">
            <RiskTrendChart points={summary.riskTrend || []} />

            <div className="flex between mt-2 xs muted">
              <span>{totalTrendEvents} total risk updates</span>
              <span>{loading ? "Refreshing..." : "Live data"}</span>
            </div>
          </div>

          <div className="section-label mt-4 mb-5">
            Top open risks
            <span className="count">{(summary.topRisks || []).length}</span>
          </div>

          <div className="card">
            {(summary.topRisks || []).length === 0 ? (
              <EmptyState
                icon="ti-shield-off"
                title="No open risks"
                desc="All risks are currently closed or no risk has been logged yet."
              />
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Risk</th>
                    <th>Study</th>
                    <th>Score</th>
                    <th>Band</th>
                  </tr>
                </thead>
                <tbody>
                  {(summary.topRisks || []).map((risk) => (
                    <tr
                      key={risk.id}
                      className="clickable-row"
                      onClick={() => handleNavigate("risk")}
                    >
                      <td>
                        <div className="medium">{risk.title}</div>
                        <div className="id xs muted">{risk.code}</div>
                      </td>
                      <td className="id xs">{risk.study}</td>
                      <td className="num bold">{risk.score}</td>
                      <td>
                        <Tag variant={risk.variant}>{risk.band}</Tag>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div style={{ width: 380, maxWidth: "100%", }}>
          <div className="section-label mb-5">Upcoming milestones</div>

          <div className="card card-pad">
            {(summary.upcomingMilestones || []).length === 0 ? (
              <EmptyState
                icon="ti-flag"
                title="No upcoming milestones"
                desc="Add milestone records in project tracking to show upcoming events."
              />
            ) : (
              <div className="timeline">
                {(summary.upcomingMilestones || []).map((milestone) => (
                  <div
                    key={milestone.id}
                    className={`timeline-item clickable-timeline-item ${milestone.status === "at_risk" ? "accent" : ""}`}
                    onClick={() => handleNavigate("project")}
                  >
                    <div className="timeline-time">
                      {milestone.date ? fmtDate(milestone.date) : "-"}
                      {" - "}
                      {Number(milestone.days) >= 0 ? `in ${milestone.days}d` : `${Math.abs(Number(milestone.days))}d overdue`}
                    </div>

                    <div className="timeline-title">{milestone.title}</div>

                    <div className="timeline-desc">
                      {milestone.study}
                      {milestone.status === "at_risk" && " - at risk"}
                      {" - "}
                      {Number(milestone.progress || 0)}% complete
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="section-label mt-4 mb-5">Recent activity</div>
          <div className="card card-pad">
            {(summary.recentActivity || []).length === 0 ? (
              <EmptyState
                icon="ti-clock"
                title="No recent activity"
                desc="Activity feed will appear as teams start working on studies and tasks."
              />
            ) : (
              <div className="timeline">
                {(summary.recentActivity || []).map((activity) => (
                  <div
                    key={activity.id}
                    className="timeline-item clickable-timeline-item"
                    onClick={() => handleNavigate(resolveActivityModule(activity))}
                  >
                    <div>
                      <div className="timeline-time">
                        {relTime(activity.at)}
                        {" - "}
                        {activity.user}
                      </div>
                      <div className="timeline-title xs medium">
                        {String(activity.action || "activity").replace(/_/g, " ")}
                      </div>
                      <div className="timeline-desc">{activity.object}</div>
                    </div>
                    <i className="ti ti-chevron-right clickable-row-arrow" />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="section-label mt-4 mb-5">Engine recommendations</div>

          <div className="card card-pad">
            {(summary.recommendations || []).map((card, index) => {
              const isWarn = card.tone === "warn";
              const isDanger = card.tone === "danger";
              const isOk = card.tone === "ok";
              const bg = isWarn
                ? "var(--warn-soft)"
                : isDanger
                  ? "var(--danger-soft)"
                  : isOk
                    ? "var(--ok-soft)"
                    : "var(--accent-soft)";
              const border = isWarn
                ? "var(--warn)"
                : isDanger
                  ? "var(--danger)"
                  : isOk
                    ? "var(--ok)"
                    : "var(--accent)";

              return (
                <div
                  key={`${card.title}-${index}`}
                  style={{
                    padding: "10px 12px",
                    background: bg,
                    borderLeft: `3px solid ${border}`,
                    marginBottom: index === (summary.recommendations || []).length - 1 ? 0 : 10,
                    fontSize: 12,
                  }}
                >
                  <div className="medium">{card.title}</div>
                  <div className="xs muted mt-1">{card.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        .clickable-row {
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .clickable-row:hover {
          background: var(--surface-2);
        }

        .clickable-timeline-item {
          cursor: pointer;
          border-radius: 6px;
          transition: transform 0.2s ease, background-color 0.2s ease;
          padding: 6px 8px;
          display: flex;
          justify-content: space-between;
        }

        .clickable-timeline-item:hover {
          transform: translateX(3px);
          background: var(--surface-2);
        }

        .clickable-coverage-card {
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .clickable-coverage-card:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
        }

        .clickable-row-arrow {
          color: var(--ink-3);
          font-size: 14px;
          opacity: 0.75;
        }

        .clickable-row:hover .clickable-row-arrow,
        .clickable-timeline-item:hover .clickable-row-arrow,
        .clickable-coverage-card:hover .clickable-row-arrow {
          opacity: 1;
          color: var(--ink);
        }
      `}</style>
    </div>
  );
}

export default DashboardModule;
